import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, StickyNote, ArrowUpRight } from "lucide-react";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { getSubjectMetadata, type SubjectColor } from "@/lib/subject-metadata";

const COLOR_ICON_BG: Record<SubjectColor, string> = {
  history: "bg-orange-100 text-orange-700",
  science: "bg-emerald-100 text-emerald-700",
  math: "bg-blue-100 text-blue-700",
  social: "bg-purple-100 text-purple-700",
  economics: "bg-amber-100 text-amber-700",
};

export default function SubjectNotes() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  if (outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="h-32 animate-pulse rounded-lg bg-muted" />
        </main>
      </div>
    );
  }

  if (outlineQuery.isError || !outlineQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Subject not found</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/subjects")}>Browse Subjects</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const { subject, units } = outlineQuery.data;
  const metadata = getSubjectMetadata(slug!);
  const Icon = metadata.iconComponent;
  const colorClass = COLOR_ICON_BG[metadata.color] || "bg-gray-100 text-gray-700";

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-primary" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to {subject.name}
            </Button>
            <Link 
              to={`/subjects/${slug}`} 
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
            >
              View all practice options <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${colorClass}`}>
               <Icon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{subject.name}</h1>
              <p className="text-muted-foreground">Notes & Resources</p>
            </div>
          </div>
        </div>

        {/* Units */}
        <div className="space-y-8">
          {units.map((unit) => (
            <Card key={unit.id} className="overflow-hidden border-none shadow-sm bg-muted/30">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-6">{unit.name}</h3>
                
                {/* Learn Section - Only Unit Notes */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">Learn</h4>
                  <Link to={`/subjects/${slug}/notes/${unit.id}`} className="block">
                    <div className="flex items-center justify-between p-3 bg-background rounded-lg border hover:border-primary/50 transition-colors cursor-pointer group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-md">
                          <StickyNote className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-medium text-sm group-hover:text-primary transition-colors">Unit Notes</div>
                          <div className="text-xs text-muted-foreground">Comprehensive summary</div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">View</Button>
                    </div>
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
